import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { changeAppLanguage } from '../../i18n';
import logoImg from '../../assets/img/entry-logo.png';
import visual from './img/visual.png';
import ultrasonic from './img/ultrasonic.png';
import measurement from './img/measurement.png';
import infrared from './img/infrared.png';
import eyesBg from './img/eyes.png';

// 移除本地 ensureEntryI18n，改用 shared helper + bundles
const entryBundles = {
  zh: {
    title: '產品介紹',
    companyName: '微覺視股份有限公司',
    visual: '視覺檢測系統',
    ultrasonic: '在線超音波除塵系統',
    measurement: '在線測厚系統',
    infrared: '在線紅外線加熱系統',
    visualDesc: 'Inspection System',
    ultrasonicDesc: 'Dust Removal Device',
    measurementDesc: 'Thickness Measurement System',
    infraredDesc: 'Process Thermal Dynamics',
    logoAlt: 'Wintriss 標誌',
    contactTitle: '聯絡資訊',
    contactAddress: '台北市北投區承德路七段401巷1009號',
    contactEmail: 'sales@iwscan.com',
    contactPhone: '886-2-2892-8000',
    copyright: '© 2025 微覺視 版權所有',
    companyTitle: '微覺視股份有限公司｜公司沿革',
    companyIntroP1: '微覺視深耕檢測設備領域超過 25 年，累積完整的光學設計、演算法開發與產線整合經驗。我們以此基礎打造全新世代的表面檢測系統，採用高速全彩工業掃描相機與改良演算法，協助客戶精準掌握材料表面品質。',
    companyIntroP2: '同時，我們亦自主開發在線 X 光測厚設備，可即時量測材料厚度與基重，提升製程穩定度。針對高階材料的潔淨需求，我們也在 2025 年推出在線超音波集塵系統，提供 99.5% 以上的清潔率，有效降低因雜質產生的製程不良率。',
    companyIntroP3: '此外，微覺視所代理的美國 PROTHERM 紅外線加熱板以高效率電力加熱方式，提供快速、均勻、節能的烘烤加熱能力，適用於各式材料處理流程。',
    commitmentTitle: '我們承諾',
    commitmentBody: '微覺視將持續以可靠度、創新技術與完善服務為核心，提供業界最先進、最穩定的檢測與自動化解決方案，與客戶攜手提升品質、降低成本、打造更具競爭力的製程。'
  },
  en: {
    title: 'Product Introduction',
    companyName: 'Wintriss Engineering Co., Ltd.',
    visual: 'Inspection System',
    ultrasonic: 'In-line Ultrasonic Cleaning System',
    measurement: 'In-line Measurement System',
    infrared: 'In-line Infrared Heating System',
    visualDesc: 'Go to current website home content',
    ultrasonicDesc: 'High-efficiency ultrasonic cleaning (placeholder)',
    measurementDesc: 'Material in-line measurement & monitoring (placeholder)',
    infraredDesc: 'Precise infrared heating control (placeholder)',
    logoAlt: 'Wintriss Engineering Logo',
    contactTitle: 'Contact',
    contactAddress: 'No.1009, Ln. 401, Sec.7, Chengde Rd., Beitou Dist., Taipei City',
    contactEmail: 'sales@iwscan.com',
    contactPhone: '+886-2-2892-8000',
    copyright: '© 2025 Wintriss Engineering. All rights reserved.',
    companyTitle: 'Wintriss Engineering Co., Ltd. | Company Profile',
    companyIntroP1: 'Wintriss Engineering has specialized in inspection equipment for over 25 years, accumulating comprehensive experience in optical design, algorithm development, and production line integration. Building on this foundation we deliver a new generation of surface inspection systems featuring high-speed full-color industrial line scan cameras and refined algorithms that help customers precisely control material surface quality.',
    companyIntroP2: 'We also independently develop in-line X-ray thickness measurement equipment that measures thickness and basis weight in real time to enhance process stability. For the cleanliness demands of advanced materials, in 2025 we launched an in-line ultrasonic dust removal system achieving over 99.5% cleaning efficiency, effectively reducing defect rates caused by contaminants.',
    companyIntroP3: 'In addition, the PROTHERM infrared heating panels represented by Wintriss Engineering use high-efficiency electrical heating to provide fast, uniform and energy-saving thermal processing performance suitable for a wide range of material treatment workflows.',
    commitmentTitle: 'Our Commitment',
    commitmentBody: 'Wintriss Engineering will continue to focus on reliability, innovative technology and comprehensive service, providing the industry’s most advanced and stable inspection and automation solutions. We work side by side with customers to improve quality, reduce cost, and build more competitive processes.'
  }
};

const btnBase = 'inline-flex items-center justify-center font-semibold tracking-wide px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600';

const Entry = () => {
  const { i18n, t: baseT } = useTranslation();
  const [ready, setReady] = useState(false);
  // Helper translation function restricted to entry namespace
  const t = (key) => baseT(key, { ns: 'entry' });

  useEffect(() => {
    const lang = i18n.language && i18n.language.toLowerCase().startsWith('zh') ? 'zh' : 'en';
    // Inject zh + en to ensure both available for immediate toggle
    ['zh', 'en'].forEach(l => {
      if (!i18n.hasResourceBundle(l, 'entry')) {
        i18n.addResourceBundle(l, 'entry', entryBundles[l], true, true);
      }
    });
    // If current language variant like zh-TW ensure fallback works
    if (lang !== i18n.language) {
      try { i18n.changeLanguage(lang); } catch (_) { /* ignore */ }
    }
    setReady(true);
  }, [i18n]);

  const items = [
    { key: 'visual', to: '/visual', accent: 'from-blue-600 to-indigo-600', bg: visual },
    { key: 'ultrasonic', to: '/ultrasonic', accent: 'from-cyan-600 to-sky-600', bg: ultrasonic },
    { key: 'measurement', to: '/measurement', accent: 'from-emerald-600 to-green-600', bg: measurement },
    { key: 'infrared', to: '/infrared', accent: 'from-amber-600 to-orange-600', bg: infrared }
  ];

  const currentLang = i18n.language || 'zh';
  const displayLang = currentLang.toLowerCase().startsWith('zh') ? 'zh' : currentLang;
  const toggleLanguage = () => {
    const newLang = displayLang === 'zh' ? 'en' : 'zh';
    changeAppLanguage(newLang);
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-gray-950 via-gray-900 to-black text-white">
      {/* 防止尚未注入資源時顯示 raw keys */}
      {!ready && (
        <div className="absolute inset-0 flex items-center justify-center text-sm text-gray-300">Loading…</div>
      )}
      {/* 右下角眼睛背景圖 */}
      <div className="pointer-events-none select-none absolute bottom-4 right-0 opacity-80 sm:opacity-80 xl:opacity-80" aria-hidden="true">
        <img src={eyesBg} alt="" className="w-32 sm:w-40 xl:w-8/12 h-auto ml-auto" loading="lazy" />
      </div>
      {ready && (
        <>
          {/* 語言切換按鈕 */}
          <div className="absolute top-6 right-6 z-20">
            <button
              onClick={toggleLanguage}
              className="px-4 py-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-lg border border-white/30 text-white font-medium transition-all duration-200 shadow-lg"
              aria-label="Switch Language"
            >
              {displayLang === 'zh' ? 'EN' : '中文'}
            </button>
          </div>
          <div className="relative flex-1 flex flex-col">
            <header className="pt-20 pb-8 text-center px-4">
              <img src={logoImg} alt={t('logoAlt')} className="w-42 h-auto mx-auto mb-3 drop-shadow-lg" loading="lazy" />
              <p className="text-sm sm:text-base tracking-wide mb-4 font-medium text-white">{t('companyName')}</p>
              <h1 className="text-3xl sm:text-4xl font-bold mb-3">{t('title')}</h1>
            </header>
            <main className="flex-1 w-full max-w-7xl mx-auto px-5 pb-12">
              {/* 兩欄：左側系統卡片，右側公司介紹；手機單欄 */}
              <div className="grid gap-10 lg:grid-cols-2 xl:gap-14">
                {/* 左側卡片區 */}
                <div>
                  <div className="grid gap-6 sm:gap-6 grid-cols-1 sm:grid-cols-2" /* RWD：手機單欄，>=sm 二欄 */>
                    {items.map(it => (
                      <div key={it.key} className="group relative rounded-xl overflow-hidden shadow-lg border border-gray-700 bg-gray-900">
                        {/* 背景圖片層 */}
                        {it.bg && (
                          <div className="absolute inset-0">
                            <img src={it.bg} alt="" className="w-full h-full object-cover transform scale-100 group-hover:scale-105 transition-transform duration-500 ease-out" loading="lazy" />
                            <div className="absolute inset-0 bg-black/70 group-hover:bg-black/60 transition-colors" />
                          </div>
                        )}
                        {/* 內容層 */}
                        <div className="relative z-10 flex flex-col justify-between h-full">
                          <div className="p-5 flex-1">
                            <h2 className="text-lg font-semibold mb-3 tracking-wide">{t(it.key)}</h2>
                            {/* 英文版時隱藏小字描述 */}
                            {displayLang === 'zh' && (
                              <p className="text-xs text-gray-300 leading-relaxed max-w-[92%]">{t(it.key + 'Desc')}</p>
                            )}
                          </div>
                          <div className="p-4">
                            <Link
                              to={it.to}
                              className={btnBase + ' text-white bg-gradient-to-r ' + it.accent + ' shadow hover:opacity-90 text-sm w-full'}
                            >
                              {t(it.key)}
                            </Link>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                {/* 右側公司介紹 */}
                <div className="space-y-5 text-sm sm:text-base leading-relaxed">
                  <h2 className="text-xl sm:text-2xl font-bold tracking-wide">{t('companyTitle')}</h2>
                  <p className="text-gray-300 whitespace-pre-line">{t('companyIntroP1')}</p>
                  <p className="text-gray-300 whitespace-pre-line">{t('companyIntroP2')}</p>
                  <p className="text-gray-300 whitespace-pre-line">{t('companyIntroP3')}</p>
                  <div className="pt-2">
                    <h3 className="text-lg font-semibold mb-2">{t('commitmentTitle')}</h3>
                    <p className="text-gray-200 whitespace-pre-line">{t('commitmentBody')}</p>
                  </div>
                </div>
              </div>
            </main>
            {/* 底部水平聯絡列 */}
            <div className="w-full mt-auto border-t border-gray-800 bg-black/40 backdrop-blur-sm px-4">
              <div className="max-w-5xl mx-auto flex flex-wrap items-center justify-center gap-3 py-5 text-[11px] sm:text-xs text-gray-300">
                <span className="font-medium">{t('contactAddress')}</span>
                <span className="hidden sm:inline text-gray-600">|</span>
                <a href="mailto:sales@iwscan.com" className="underline decoration-gray-500 hover:decoration-white transition">{t('contactEmail')}</a>
                <span className="hidden sm:inline text-gray-600">|</span>
                <a href="tel:+886228928000" className="hover:text-white transition">{t('contactPhone')}</a>
                <span className="hidden sm:inline text-gray-600">|</span>
                <span className="text-gray-400">{t('copyright')}</span>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Entry;
